import { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, RefreshCw, Maximize2, Download, Check, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { StepIndicator } from '@/components/StepIndicator';
import { UploadedImage, GenerationResult, GenerationType, RoomType, StagingStyle, CatalogDebug, CatalogPlan } from '@/types';
import { ROOMS, getRoomLabel, STAGING_STYLES, supportsStaging } from '@/lib/roomConfig';
import { supabase } from '@/lib/supabase';
import { findUnknownPlaceholders } from '@/lib/promptPlaceholders';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import HeicSafeImage from "@/components/ui/HeicSafeImage";
import { CatalogAnalyzePanel } from '@/components/views/CatalogAnalyzePanel';
import { showStagingCard } from '@/lib/stagingFlow';

interface ReviewViewProps {
  uploadedImages: UploadedImage[];
  results: GenerationResult[];
  onRegenerate: (imgId: string, type: GenerationType, currentStoragePath?: string, emptyConsultOverride?: string) => void;
  onEdit: (imgId: string, type: GenerationType, currentStoragePath?: string) => void;
  onRegenerateWithFeedback?: (imgId: string, type: GenerationType, feedback: string, sourceImagePath?: string) => void;
  onVariantSelect?: (imgId: string, type: GenerationType, variantId: string) => void;
  onStageApproved: (imgId: string, opts?: { catalogPlan?: CatalogPlan }) => void;
  onCatalogAnalyze?: (imgId: string, consultOverride: string, renderTemplateOverride: string) => Promise<CatalogDebug | null>;
  stagingStyle?: StagingStyle;
  onStagingStyleChange?: (style: StagingStyle) => void;
  catalogStagingEnabled?: boolean;
  onNext: () => void;
  onBack?: () => void;
  onImageClick: (url: string) => void;
  onDownloadSingle?: (url: string, filename: string) => void;
  isAdmin?: boolean;
  disabledFeatures?: string[];
}

export const ReviewView: React.FC<ReviewViewProps> = ({
  uploadedImages,
  results,
  onRegenerate,
  onEdit,
  onVariantSelect,
  onStageApproved,
  onCatalogAnalyze,
  stagingStyle,
  onStagingStyleChange,
  catalogStagingEnabled,
  isAdmin,
  onNext,
  onBack,
  onImageClick,
  onDownloadSingle,
}) => {
  const isAnyLoading = results.some((r) => r.isLoading);
  // Guards against a fast double-click enqueuing two staged generations for
  // the same image before the staged result placeholder shows up in `results`.
  const [stagingInFlight, setStagingInFlight] = useState<Set<string>>(new Set());
  // Admin-only: tracks which images have the Katalog-Analyse panel expanded.
  const [analyzeOpen, setAnalyzeOpen] = useState<Set<string>>(new Set());
  // Admin-only: Leerräum-Berater-Override pro Bild — geht bei „Neu generieren" (Leer)
  // Leerräum-Berater-Prompt: pro Bild editierbar; Feld ist immer mit dem
  // gespeicherten DB-Prompt (empty_consult_system) vorbefüllt, „Neu generieren"
  // nutzt den Feldinhalt, „Speichern" schreibt ihn dauerhaft zurück.
  const [emptyOverrides, setEmptyOverrides] = useState<Record<string, string>>({});
  const [emptyDbPrompt, setEmptyDbPrompt] = useState('');
  const [savingEmptyPrompt, setSavingEmptyPrompt] = useState(false);
  const [emptySaveMsg, setEmptySaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // DB-Standard einmalig laden, damit die Textareas den echten Prompt zeigen (Admin-only).
  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    supabase
      .from('prompt_templates')
      .select('content')
      .eq('key', 'empty_consult_system')
      .single()
      .then(({ data }) => { if (active && data?.content) setEmptyDbPrompt(data.content as string); });
    return () => { active = false; };
  }, [isAdmin]);

  const saveEmptyPrompt = async (imgId: string, content: string) => {
    const unknown = findUnknownPlaceholders(content, 'empty_consult');
    if (unknown.length) {
      setEmptySaveMsg({ ok: false, text: `Unbekannte Platzhalter: ${unknown.join(', ')}` });
      return;
    }
    setSavingEmptyPrompt(true);
    setEmptySaveMsg(null);
    try {
      const { error } = await supabase.from('prompt_templates').update({ content }).eq('key', 'empty_consult_system');
      if (error) throw error;
      setEmptyDbPrompt(content);
      setEmptyOverrides((prev) => { const next = { ...prev }; delete next[imgId]; return next; });
      setEmptySaveMsg({ ok: true, text: 'Gespeichert' });
    } catch (e) {
      setEmptySaveMsg({ ok: false, text: e instanceof Error ? e.message : 'Speichern fehlgeschlagen' });
    } finally {
      setSavingEmptyPrompt(false);
    }
  };
  const toggleAnalyze = (imgId: string) =>
    setAnalyzeOpen((prev) => {
      const next = new Set(prev);
      next.has(imgId) ? next.delete(imgId) : next.add(imgId);
      return next;
    });

  const handleStageClick = (imgId: string, catalogPlan?: CatalogPlan) => {
    if (stagingInFlight.has(imgId)) return;
    setStagingInFlight((prev) => new Set(prev).add(imgId));
    onStageApproved(imgId, catalogPlan ? { catalogPlan } : undefined);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <StepIndicator currentStep="review" onBack={onBack} />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Überprüfen & Auswählen</h2>
          <p className="text-muted-foreground">Wähle für jeden Raum die beste Variante aus.</p>
        </div>
        <div className="flex gap-3">
          {onBack && (
            <Button variant="ghost" onClick={onBack} disabled={isAnyLoading} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Zurück
            </Button>
          )}
          <Button onClick={onNext} disabled={isAnyLoading} className="flex items-center gap-2">
            {isAnyLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generierung läuft…</>
            ) : (
              <>Weiter: Upscaling <ArrowRight className="w-4 h-4" /></>
            )}
          </Button>
        </div>
      </div>

      {uploadedImages.map((img) => {
        const emptyRes = results.find(
          (r) => r.originalImageId === img.id && r.type === GenerationType.EMPTY
        );
        const stagedRes = results.find(
          (r) => r.originalImageId === img.id && r.type === GenerationType.STAGED
        );

        const roomId = img.roomType as string;
        const actualLabel =
          img.roomType === RoomType.CUSTOM && img.customLabel
            ? img.customLabel
            : getRoomLabel(roomId);

        const hasEmpty = !!emptyRes;
        const hasStaged = !!stagedRes;
        const hasBoth = hasEmpty && hasStaged;
        const hasNoGeneration = !hasEmpty && !hasStaged;
        const roomConfig = ROOMS[roomId];
        const isOutdoor = roomConfig?.category === 'outdoor';
        const canStageRoom = supportsStaging(roomId);
        const emptyReady = !!emptyRes && !emptyRes.isLoading && !!emptyRes.currentUrl;
        const stagingInFlightRoom = stagingInFlight.has(img.id);
        // Card is shown from the start for stageable, not-yet-staged rooms; it stays
        // disabled while the empty is still generating (or while staging is in flight).
        const stageDisabled = !emptyReady || stagingInFlightRoom;
        const stagingCardVisible = showStagingCard({
          v2Active: !!catalogStagingEnabled,
          canStage: canStageRoom,
          hasStaged,
        });

        return (
          <Card key={img.id}>
            <CardContent className="p-6">
              {/* Room header */}
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                <h3 className="font-bold text-lg text-foreground">{actualLabel}</h3>
                {isOutdoor && (
                  <Badge variant="outline" className="bg-sky-100 text-sky-700 hover:bg-sky-100 border-none">
                    Außenbereich
                  </Badge>
                )}
                {img.keepKitchen && (
                  <Badge variant="outline" className="bg-green-100 text-green-700 hover:bg-green-100 border-none">
                    Küche erhalten
                  </Badge>
                )}
                {hasNoGeneration && (
                  <Badge variant="outline" className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-none">
                    Keine Generierung
                  </Badge>
                )}
                {!hasStaged && hasEmpty && (
                  <Badge variant="outline" className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none">
                    Nur Leer
                  </Badge>
                )}
              </div>

              {/* Layout: Original | Leer | Eingerichtet — all side by side */}
              <div className={`grid grid-cols-1 gap-4 ${hasBoth || stagingCardVisible ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
                {/* Original */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-muted-foreground">Original</p>
                  <div
                    className="aspect-[4/3] rounded-xl overflow-hidden bg-muted border border-border cursor-pointer group relative"
                    onClick={() => onImageClick(img.previewUrl)}
                  >
                    <HeicSafeImage
                      src={img.previewUrl}
                      className="w-full h-full object-cover"
                      alt="Original"
                    />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 flex items-center justify-center transition-colors">
                      <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                  </div>
                  {hasNoGeneration && (
                    <p className="text-xs text-muted-foreground italic">
                      Keine Generierung — Original wird im Branding-Schritt verwendet.
                    </p>
                  )}
                </div>

                {hasEmpty && (
                  <div className="space-y-2">
                    <VariantSection
                      title="Leer"
                      result={emptyRes!}
                      imgId={img.id}
                      roomType={actualLabel}
                      onRegenerate={() => onRegenerate(img.id, GenerationType.EMPTY, emptyRes?.storagePath, emptyOverrides[img.id] ?? emptyDbPrompt)}
                      onEdit={() => onEdit(img.id, GenerationType.EMPTY, emptyRes?.storagePath)}
                      onVariantSelect={onVariantSelect
                        ? (variantId) => onVariantSelect(img.id, GenerationType.EMPTY, variantId)
                        : undefined}
                      onImageClick={onImageClick}
                      onDownloadSingle={onDownloadSingle}
                    />
                    {isAdmin && (
                      <div className="space-y-1">
                        <label className="block text-[11px] font-medium text-slate-600">
                          Leerräum-Berater-Prompt (Admin) — „Neu generieren" nutzt diesen Text, „Speichern" macht ihn dauerhaft
                        </label>
                        <textarea
                          value={emptyOverrides[img.id] ?? emptyDbPrompt}
                          onChange={(e) => setEmptyOverrides((prev) => ({ ...prev, [img.id]: e.target.value }))}
                          rows={6}
                          className="w-full rounded-md border border-input bg-background p-2 text-[11px] font-mono leading-snug"
                          placeholder="Berater-Prompt wird geladen…"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={savingEmptyPrompt}
                            onClick={() => saveEmptyPrompt(img.id, emptyOverrides[img.id] ?? emptyDbPrompt)}
                          >
                            {savingEmptyPrompt ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                            💾 Berater-Prompt speichern
                          </Button>
                          {emptySaveMsg && (
                            <span className={emptySaveMsg.ok ? 'text-emerald-600 text-[11px]' : 'text-red-600 text-[11px]'}>
                              {emptySaveMsg.text}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {hasStaged ? (
                  <VariantSection
                    title="Virtuell Eingerichtet"
                    result={stagedRes!}
                    imgId={img.id}
                    roomType={actualLabel}
                    onRegenerate={() => onRegenerate(img.id, GenerationType.STAGED, stagedRes?.storagePath)}
                    onEdit={() => onEdit(img.id, GenerationType.STAGED, stagedRes?.storagePath)}
                    onVariantSelect={onVariantSelect
                      ? (variantId) => onVariantSelect(img.id, GenerationType.STAGED, variantId)
                      : undefined}
                    onImageClick={onImageClick}
                    onDownloadSingle={onDownloadSingle}
                  />
                ) : stagingCardVisible ? (
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-muted-foreground">Virtuell Eingerichtet</p>
                    {onStagingStyleChange && (
                      <select
                        value={stagingStyle ?? 'modern'}
                        onChange={(e) => onStagingStyleChange(e.target.value as StagingStyle)}
                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        {STAGING_STYLES.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => handleStageClick(img.id)}
                      disabled={stageDisabled}
                      className="group relative flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 rounded-[20px] border-2 border-dashed border-[#EBEBE6] bg-card transition-colors hover:border-primary/60 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-[#EBEBE6]"
                    >
                      <div className="mb-2 flex items-center justify-center rounded-full bg-[#FFF3EC] p-[10px] group-hover:bg-[#FFD4BF] transition-colors">
                        {stagingInFlightRoom || !emptyReady ? (
                          <Loader2 className="h-9 w-9 text-primary animate-spin" strokeWidth={2} />
                        ) : (
                          <Sparkles className="h-9 w-9 text-primary" strokeWidth={2} />
                        )}
                      </div>
                      <p className="font-medium text-[14px] tracking-[-0.14px] text-foreground">
                        {stagingInFlightRoom ? 'Staging läuft…' : !emptyReady ? 'Wird vorbereitet…' : 'Staging erstellen'}
                      </p>
                      <p className="font-normal text-[12px] tracking-[-0.12px] text-muted-foreground">
                        {!emptyReady && !stagingInFlightRoom ? 'Warte auf leergeräumtes Bild' : 'Möbliert auf dem leeren Bild'}
                      </p>
                    </button>
                    {isAdmin && onCatalogAnalyze && (
                      <Button size="sm" variant="outline" className="w-full" disabled={!emptyReady} onClick={() => toggleAnalyze(img.id)}>
                        <Wand2 className="w-4 h-4 mr-2" />
                        {analyzeOpen.has(img.id) ? 'Analyse schließen' : 'Analysieren'}
                      </Button>
                    )}
                    {isAdmin && onCatalogAnalyze && analyzeOpen.has(img.id) && (
                      <CatalogAnalyzePanel
                        onAnalyze={(c, r) => onCatalogAnalyze(img.id, c, r)}
                        onGenerateWithPlan={(plan) => { handleStageClick(img.id, plan); }}
                      />
                    )}
                  </div>
                ) : null}
              </div>
              {isAdmin && emptyRes?.catalogDebug && (emptyRes.catalogDebug.effectiveEmptyPrompt !== undefined || emptyRes.catalogDebug.emptyConsult) && (
                <EmptyConsultDebugPanel debug={emptyRes.catalogDebug} />
              )}
              {isAdmin && stagedRes?.catalogDebug && (
                <>
                  {stagedRes.catalogDebug.ran && <CatalogDebugPanel debug={stagedRes.catalogDebug} />}
                  {(stagedRes.catalogDebug.effectiveEmptyPrompt !== undefined || stagedRes.catalogDebug.emptyConsult) && (
                    <EmptyConsultDebugPanel debug={stagedRes.catalogDebug} />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Admin-only: zeigt, was der Katalog-Staging-Berater gewählt hat, den Render-Prompt
// und — falls auf den Standard-Prompt zurückgefallen — den Grund.
const CatalogDebugPanel: React.FC<{ debug: CatalogDebug }> = ({ debug }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 font-medium text-foreground"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Katalog-Staging (Admin-Diagnose)
          {debug.used ? (
            <Badge className="bg-emerald-500/15 text-emerald-600 border-transparent">Katalog genutzt</Badge>
          ) : (
            <Badge className="bg-amber-500/15 text-amber-600 border-transparent">Fallback: Standard-Prompt</Badge>
          )}
        </span>
        <span className="text-xs text-muted-foreground">{open ? 'einklappen' : 'ausklappen'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            {debug.candidateCount} Moodboard-Kandidat(en) verfügbar.
          </p>
          {!debug.used && debug.fallbackReason && (
            <p className="text-sm text-amber-600">⚠ {debug.fallbackReason}</p>
          )}
          {debug.selected.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Gewählte Möbel ({debug.selected.length})
              </p>
              <ul className="space-y-1">
                {debug.selected.map((s, i) => (
                  <li key={i} className="text-sm">
                    <span className="font-medium">Bild {i + 2}</span> · {s.category}
                    {s.name ? ` — ${s.name}` : ''} →{' '}
                    <span className="text-muted-foreground">{s.positionHint}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {debug.notes && (
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold">Notiz:</span> {debug.notes}
            </p>
          )}
          {debug.renderPrompt && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Render-Prompt anzeigen</summary>
              <pre className="mt-1 whitespace-pre-wrap rounded bg-muted/50 p-2 text-[11px] leading-snug">
                {debug.renderPrompt}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// Admin-only: Leerräum-Berater-Diagnose — zeigt das finale Leer-Prompt (ging exakt
// so ans Bild-Modell) sowie Basis-Template und Berater-Output (Spiegel der
// effectiveConsultPrompt-Darstellung im Katalog-Panel).
const EmptyConsultDebugPanel: React.FC<{ debug: CatalogDebug }> = ({ debug }) => {
  const [open, setOpen] = useState(true);
  const ec = debug.emptyConsult;
  return (
    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 font-medium text-foreground"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          Leerräum-Berater (Admin-Diagnose)
          {ec?.usedOverride ? (
            <Badge className="bg-amber-500/15 text-amber-600 border-transparent">Override genutzt</Badge>
          ) : (
            <Badge className="bg-emerald-500/15 text-emerald-600 border-transparent">DB-Prompt</Badge>
          )}
        </span>
        <span className="text-xs text-muted-foreground">{open ? 'einklappen' : 'ausklappen'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {ec && (
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold">Berater-Modell:</span> {ec.model}
            </p>
          )}
          {debug.effectiveEmptyPrompt !== undefined && (
            <details className="text-xs" open>
              <summary className="cursor-pointer text-muted-foreground">Finales Leer-Prompt (ging exakt so ans Bild-Modell)</summary>
              <pre className="mt-1 whitespace-pre-wrap rounded bg-muted/50 p-2 text-[11px] leading-snug">
                {debug.effectiveEmptyPrompt}
              </pre>
            </details>
          )}
          {ec && (
            <>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Basis-Template (Berater-Input)</summary>
                <pre className="mt-1 whitespace-pre-wrap rounded bg-muted/50 p-2 text-[11px] leading-snug">
                  {ec.base}
                </pre>
              </details>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Berater-Output</summary>
                <pre className="mt-1 whitespace-pre-wrap rounded bg-muted/50 p-2 text-[11px] leading-snug">
                  {ec.output}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// VariantSection — Shows main image + action buttons + variant thumbnails
// ============================================================================

interface VariantSectionProps {
  title: string;
  result: GenerationResult;
  imgId: string;
  roomType: string;
  onRegenerate: () => void;
  onEdit: () => void;
  onVariantSelect?: (variantId: string) => void;
  onImageClick: (url: string) => void;
  onDownloadSingle?: (url: string, filename: string) => void;
}

const VariantSection: React.FC<VariantSectionProps> = ({
  title,
  result,
  imgId,
  roomType,
  onRegenerate,
  onEdit,
  onVariantSelect,
  onImageClick,
  onDownloadSingle,
}) => {
  const variants = result.variants;
  const hasVariants = variants && variants.length > 1;
  const selectedId = result.selectedVariantId;

  const handleDownload = () => {
    if (result.currentUrl && onDownloadSingle) {
      const suffix = result.type === GenerationType.EMPTY ? 'Leer' : 'Eingerichtet';
      const filename = `${roomType}_${imgId.slice(-4)}_${suffix}.png`;
      onDownloadSingle(result.currentUrl, filename);
    }
  };

  return (
    <div className="space-y-2">
      {/* Header: title + status + download */}
      <div className="flex items-center justify-between min-h-[1.5rem]">
        <p className="text-sm font-semibold text-muted-foreground">{title}</p>
        <div className="flex items-center gap-1">
          {result.isLoading ? (
            <span className="text-xs text-primary flex items-center gap-1.5 font-medium">
              <Loader2 className="w-3 h-3 animate-spin" />
              {result.currentUrl ? 'Regenerierung…' : 'Generierung…'}
            </span>
          ) : result.currentUrl && onDownloadSingle ? (
            <Button variant="ghost" size="icon" onClick={handleDownload} className="h-6 w-6" title="Herunterladen">
              <Download className="w-3 h-3" />
            </Button>
          ) : null}
        </div>
      </div>

      {/* Main image */}
      <div
        className="aspect-[4/3] rounded-xl overflow-hidden bg-muted border border-border relative group cursor-pointer"
        onClick={() => !result.isLoading && result.currentUrl && onImageClick(result.currentUrl)}
      >
        {result.isLoading && result.currentUrl ? (
          <>
            <img src={result.currentUrl} className="w-full h-full object-cover opacity-40" alt={title} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-primary animate-spin" />
              </div>
              <span className="text-xs font-semibold text-white drop-shadow bg-black/40 px-3 py-1 rounded-full">
                KI generiert…
              </span>
            </div>
          </>
        ) : result.isLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted text-muted-foreground gap-3">
            <div className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary animate-pulse" />
            </div>
            <span className="text-xs font-medium">KI-Generierung…</span>
          </div>
        ) : result.currentUrl ? (
          <>
            <img src={result.currentUrl} className="w-full h-full object-cover" alt={title} />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/10 flex items-center justify-center transition-colors">
              <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
            </div>
          </>
        ) : result.error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
            <p className="text-red-500 text-sm mb-3">{result.error}</p>
            <Button variant="outline" size="sm" onClick={onRegenerate} className="gap-1.5">
              <RefreshCw className="w-3 h-3" /> Erneut versuchen
            </Button>
          </div>
        ) : null}
      </div>

      {/* Action buttons — only when an image exists and isn't loading */}
      {!result.isLoading && result.currentUrl && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerate}
            className="gap-1.5 text-xs font-medium"
            title="Neu generieren — gleicher Prompt, neues Ergebnis"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Neu generieren
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="gap-1.5 text-xs font-medium border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
            title="Bearbeiten — verfeinere mit eigenen Anweisungen"
          >
            <Wand2 className="w-3.5 h-3.5" />
            Bearbeiten
          </Button>
        </div>
      )}

      {/* Variant picker thumbnails */}
      {hasVariants && (
        <div className={`space-y-1.5 transition-opacity ${result.isLoading ? 'opacity-40 pointer-events-none' : ''}`}>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {variants.map((variant, index) => {
              const isSelected = selectedId === variant.id;
              return (
                <button
                  key={variant.id}
                  onClick={() => onVariantSelect?.(variant.id)}
                  className={`relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                    isSelected
                      ? 'border-primary ring-2 ring-primary/20 shadow-sm'
                      : 'border-border opacity-60 hover:opacity-100 hover:border-primary/40'
                  }`}
                  title={`Variante ${String.fromCharCode(65 + index)}`}
                >
                  <img src={variant.url} className="w-full h-full object-cover" alt={`Variante ${String.fromCharCode(65 + index)}`} />
                  {isSelected && (
                    <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow">
                      <Check className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                  <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] text-center py-0.5 font-medium">
                    {String.fromCharCode(65 + index)}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            Wähle die beste Variante — diese wird für Upscaling und Branding verwendet.
          </p>
        </div>
      )}
    </div>
  );
};
